import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { renderStrandShowcase, bindStrandShowcase } from '../components/StrandShowcase';
import { rarityColor, buildItemTooltip } from '../components/TooltipContent';
import { renderItemIcon } from '../components/ItemIcon';
import { Tooltip } from '../components/Tooltip';
import { getHubBg } from '../components/NpcBriefingModal';
import { formatUnitClass, formatDefensiveTypes } from '../components/GameTerms';
import type {
  InspectData, InspectUnit, InspectItemData, ConfigData, PresenceData,
  ComputedStats, ItemData, PartyUnit,
} from '../../types';

/** Dev-mode mock data for players that don't exist in the DB. */
const MOCK_STATS = {} as ComputedStats;
const MOCK_ITEM = (name: string, baseType: string, cat: 'weapon' | 'armor' | 'accessory', rarity = 'normal', level = 5): InspectItemData => ({
  name, baseType, category: cat, rarity, level, implicitAffix: '{}', explicitAffixes: '[]',
});

const MOCK_INSPECT: Record<string, InspectData> = {
  'mock-user-1': {
    discordId: 'mock-user-1', username: 'Aelindra', level: 12, campaignStep: 3,
    party: [
      { unitClass: 'apprentice', name: 'Sylvara', race: 'human', element: 'fire', level: 10, rarity: 'rare', computedStats: MOCK_STATS, weapon: MOCK_ITEM('Ash Staff', 'oak_staff', 'weapon', 'rare', 10), armor: MOCK_ITEM('Silk Robe', 'mystic_robe', 'armor', 'magic', 8), accessory: null, skillIds: ['ignite', 'fire_blast'], masteryRanks: { pyromancy: 5, sorcery: 3 }, activeSkinId: null },
      { unitClass: 'warrior', name: 'Haldric', race: 'human', element: 'steel', level: 8, rarity: 'magic', computedStats: MOCK_STATS, weapon: MOCK_ITEM('Iron Sword', 'shortsword', 'weapon', 'magic', 8), armor: MOCK_ITEM('Cuirass', 'cuirass', 'armor', 'normal', 6), accessory: MOCK_ITEM('Buckler', 'buckler', 'accessory', 'normal', 5), skillIds: ['slash', 'harden'], masteryRanks: { bladecraft: 4, bulwark: 2 }, activeSkinId: null },
      { unitClass: 'cleric', name: 'Maren', race: 'human', element: 'holy', level: 9, rarity: 'normal', computedStats: MOCK_STATS, weapon: MOCK_ITEM('War Hammer', 'war_hammer', 'weapon', 'normal', 7), armor: null, accessory: null, skillIds: ['heal', 'smite'], masteryRanks: { theurgy: 3, macecraft: 1 }, activeSkinId: null },
    ],
  },
  'mock-user-2': {
    discordId: 'mock-user-2', username: 'Stoneguard', level: 6, campaignStep: 1,
    party: [
      { unitClass: 'juggernaut', name: 'Gryndel', race: 'granocen', element: 'brute', level: 5, rarity: 'magic', computedStats: MOCK_STATS, weapon: MOCK_ITEM('Stone Mace', 'war_hammer', 'weapon', 'magic', 5), armor: MOCK_ITEM('Heavy Plate', 'war_plate', 'armor', 'magic', 5), accessory: null, skillIds: ['smash', 'challenge'], masteryRanks: { macecraft: 3, fortification: 2 }, activeSkinId: null },
      { unitClass: 'apprentice', name: 'Elowen', race: 'human', element: 'fire', level: 4, rarity: 'normal', computedStats: MOCK_STATS, weapon: MOCK_ITEM('Oak Staff', 'oak_staff', 'weapon'), armor: null, accessory: null, skillIds: ['ignite'], masteryRanks: { pyromancy: 2, sorcery: 1 }, activeSkinId: null },
      { unitClass: 'cleric', name: 'Bertram', race: 'human', element: 'holy', level: 4, rarity: 'normal', computedStats: MOCK_STATS, weapon: MOCK_ITEM('War Hammer', 'war_hammer', 'weapon'), armor: null, accessory: null, skillIds: ['heal'], masteryRanks: { theurgy: 1 }, activeSkinId: null },
    ],
  },
};

const SLOT_LABELS: Record<string, string> = {
  weapon: 'Weapon',
  armor: 'Armor',
  offhand: 'Offhand',
  accessory: 'Accessory',
};

export class InspectScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'inspect',
    name: 'Inspect',
    persistent: false,
    needsPhaser: false,
  };

  private inspectData: InspectData | null = null;
  private loading = false;

  constructor() {
    super({ rootId: 'inspect-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'inspect-root';
    root.innerHTML = `
      <div class="inspect-screen">
        <div class="inspect-stage">
          <div class="inspect-bg"></div>
          <div class="inspect-header">
            <button class="inspect-back-btn" type="button">&larr;</button>
            <span class="screen-title">Inspect</span>
          </div>
          <div class="party-showcase inspect-party-showcase"></div>
        </div>
        <div class="inspect-flyout hidden" aria-hidden="true">
          <div class="inspect-flyout-backdrop"></div>
          <div class="inspect-flyout-panel">
            <button class="inspect-flyout-close" type="button" aria-label="Close">&times;</button>
            <div class="inspect-flyout-content"></div>
          </div>
        </div>
        <div class="inspect-empty hidden">Player not found or has not played yet.</div>
        <div class="inspect-loading hidden">Loading...</div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    this.$('.inspect-back-btn')?.addEventListener('click', () => {
      ScreenRouter.back();
    });
    this.$('.inspect-flyout-backdrop')?.addEventListener('click', () => this.closeFlyout());
    this.$('.inspect-flyout-close')?.addEventListener('click', () => this.closeFlyout());
    const bgEl = this.$('.inspect-bg') as HTMLElement | null;
    if (bgEl) {
      bgEl.style.backgroundImage = `url("${getHubBg()}")`;
    }
  }

  protected setupSubscriptions(): void {}

  async onEnter(params?: Record<string, unknown>): Promise<void> {
    const discordId = params?.discordId as string;
    if (!discordId) {
      ScreenRouter.back();
      return;
    }

    const presence = StateManager.get('presenceData') as PresenceData | undefined;
    const participant = presence?.participants.find(p => p.id === discordId);
    const titleEl = this.$('.screen-title');
    if (titleEl && participant) {
      titleEl.textContent = participant.global_name || participant.nickname || participant.username;
    }

    await this.loadInspectData(discordId);
  }

  onLeave(): void {
    Tooltip.hide();
    this.closeFlyout();
  }

  private async loadInspectData(discordId: string): Promise<void> {
    this.loading = true;
    this.inspectData = null;
    this.onRender();

    if (MOCK_INSPECT[discordId]) {
      this.inspectData = MOCK_INSPECT[discordId];
      this.loading = false;
      this.onRender();
      return;
    }

    const result = await APIManager.inspectPlayer(discordId);
    this.loading = false;

    if (result.success && result.data) {
      this.inspectData = result.data;
    }
    this.onRender();
  }

  protected onRender(): void {
    const stage = this.$('.inspect-party-showcase');
    const loadingEl = this.$('.inspect-loading');
    const emptyEl = this.$('.inspect-empty');
    if (!stage) return;

    const showEmpty = !this.loading && (!this.inspectData || this.inspectData.party.length === 0);
    loadingEl?.classList.toggle('hidden', !this.loading);
    emptyEl?.classList.toggle('hidden', !showEmpty);

    if (this.loading || !this.inspectData) {
      stage.innerHTML = '';
      return;
    }

    const d = this.inspectData;
    const configData = StateManager.get('configData') as ConfigData | undefined;

    const titleEl = this.$('.screen-title');
    if (titleEl) {
      const campLabel = d.campaignStep > 0 ? `Ch. ${d.campaignStep}` : 'New';
      titleEl.innerHTML = `${d.username} <span class="inspect-title-meta">Lv.${d.level} · ${campLabel}</span>`;
    }

    if (d.party.length === 0) {
      stage.innerHTML = '<div class="inspect-empty">No party equipped</div>';
      return;
    }

    stage.innerHTML = d.party
      .map((u, i) => renderStrandShowcase({
        unit: this.toShowcaseUnit(u, i),
        configData,
        swappable: true,
        swapLabel: 'Details',
        extraClasses: 'party-member--inspect',
        aboveHead: this.renderAboveHead(u, configData),
      }))
      .join('');

    // Bind tap → flyout, attach item tooltips on chips
    stage.querySelectorAll<HTMLElement>('.party-member--inspect').forEach((el, idx) => {
      bindStrandShowcase(el, {
        onSwap: () => this.openFlyout(idx),
      });
    });

    this.attachChipTooltips(stage, d, configData);
  }

  // ─── Above-head info: top 2 mastery pills + 4 equipment chips ───

  private renderAboveHead(unit: InspectUnit, config?: ConfigData): string {
    const masteryEntries = Object.entries(unit.masteryRanks ?? {})
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2);

    const masteryHTML = masteryEntries
      .map(([id, rank]) => {
        const def = config?.masteries?.[id];
        return `<span class="inspect-mastery-pill">${def?.name ?? id} ${rank}</span>`;
      })
      .join('');

    const slots: { slot: string; item: InspectItemData | null }[] = [
      { slot: 'weapon', item: unit.weapon },
      { slot: 'armor', item: unit.armor },
      { slot: 'offhand', item: (unit as InspectUnit & { offhand?: InspectItemData | null }).offhand ?? null },
      { slot: 'accessory', item: unit.accessory ?? null },
    ];

    const chipsHTML = slots
      .map(({ slot, item }) => {
        if (!item) {
          return `<div class="inspect-mini-chip inspect-mini-chip--empty" data-slot="${slot}" title="${SLOT_LABELS[slot]}"></div>`;
        }
        const rColor = rarityColor(item.rarity ?? 'normal');
        const icon = renderItemIcon(item.baseType, item.category, 'sm');
        return `<div class="inspect-mini-chip inspect-mini-chip--filled" data-slot="${slot}" style="--rarity-color:${rColor}">${icon}</div>`;
      })
      .join('');

    return `
      ${masteryHTML ? `<div class="inspect-above-row inspect-above-mastery">${masteryHTML}</div>` : ''}
      <div class="inspect-above-row inspect-above-chips">${chipsHTML}</div>
    `;
  }

  private attachChipTooltips(stage: HTMLElement, data: InspectData, config?: ConfigData): void {
    stage.querySelectorAll<HTMLElement>('.party-member--inspect').forEach((member, idx) => {
      const unit = data.party[idx];
      if (!unit) return;
      member.querySelectorAll<HTMLElement>('.inspect-mini-chip--filled').forEach(chip => {
        const slot = chip.dataset.slot;
        let item: InspectItemData | null = null;
        if (slot === 'weapon') item = unit.weapon;
        else if (slot === 'armor') item = unit.armor;
        else if (slot === 'offhand') item = (unit as InspectUnit & { offhand?: InspectItemData | null }).offhand ?? null;
        else if (slot === 'accessory') item = unit.accessory ?? null;
        if (!item) return;
        Tooltip.attach(chip, () => buildItemTooltip(this.toItemData(item!), config));
        // Stop chip click from bubbling up to strand → flyout
        chip.addEventListener('click', (e) => e.stopPropagation());
      });
    });
  }

  // ─── Detail flyout (full stats, all masteries, skills) ───

  private openFlyout(unitIndex: number): void {
    const flyout = this.$('.inspect-flyout');
    const content = this.$('.inspect-flyout-content');
    if (!flyout || !content || !this.inspectData) return;
    const unit = this.inspectData.party[unitIndex];
    if (!unit) return;

    const config = StateManager.get('configData') as ConfigData | undefined;
    content.innerHTML = this.renderFlyoutContent(unit, config);
    flyout.classList.remove('hidden');
    flyout.setAttribute('aria-hidden', 'false');
  }

  private closeFlyout(): void {
    const flyout = this.$('.inspect-flyout');
    if (!flyout) return;
    flyout.classList.add('hidden');
    flyout.setAttribute('aria-hidden', 'true');
    Tooltip.hide();
  }

  private renderFlyoutContent(unit: InspectUnit, config?: ConfigData): string {
    const className = unit.unitClass ? formatUnitClass(unit.unitClass) : '';
    const defensiveLabel = unit.defensiveTypes ? formatDefensiveTypes(unit.defensiveTypes) : '';

    // Stats
    const cs = unit.computedStats;
    const statRows: string[] = [];
    if (cs?.hp) statRows.push(`<div class="inspect-stat-row"><span>Health</span><b>${cs.hp}</b></div>`);
    if (cs?.guard) statRows.push(`<div class="inspect-stat-row"><span>Guard</span><b>${cs.guard}</b></div>`);
    if (cs?.spd) statRows.push(`<div class="inspect-stat-row"><span>Speed</span><b>${cs.spd}</b></div>`);
    if (cs?.equipCritChance) statRows.push(`<div class="inspect-stat-row"><span>Crit Chance</span><b>+${cs.equipCritChance.toFixed(1)}%</b></div>`);
    if (cs?.equipIncreasedCritChance) statRows.push(`<div class="inspect-stat-row"><span>Increased Crit</span><b>+${cs.equipIncreasedCritChance.toFixed(0)}%</b></div>`);
    if (cs?.equipIncreasedDamage) statRows.push(`<div class="inspect-stat-row"><span>Increased Damage</span><b>+${cs.equipIncreasedDamage.toFixed(0)}%</b></div>`);
    if (cs?.equipLifesteal) statRows.push(`<div class="inspect-stat-row"><span>Lifesteal</span><b>${cs.equipLifesteal.toFixed(1)}%</b></div>`);

    // Masteries (all invested)
    const masteryEntries = Object.entries(unit.masteryRanks ?? {})
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    const masteryHTML = masteryEntries
      .map(([id, rank]) => {
        const def = config?.masteries?.[id];
        return `<span class="inspect-flyout-mastery">${def?.name ?? id} <b>${rank}</b></span>`;
      })
      .join('');

    // Skills
    const skillsHTML = (unit.skillIds ?? [])
      .map(id => {
        const ab = config?.abilities?.[id];
        return `<span class="inspect-flyout-skill">${ab?.name ?? id}</span>`;
      })
      .join('');

    return `
      <div class="inspect-flyout-header">
        <div class="inspect-flyout-name" style="color:${rarityColor(unit.rarity)}">${unit.name}</div>
        <div class="inspect-flyout-meta">Lv.${unit.level} · ${className}${defensiveLabel ? ` · ${defensiveLabel}` : ''}</div>
      </div>
      ${statRows.length > 0 ? `
        <div class="inspect-flyout-section">
          <div class="inspect-flyout-section-label">Stats</div>
          <div class="inspect-flyout-stats">${statRows.join('')}</div>
        </div>
      ` : ''}
      ${masteryHTML ? `
        <div class="inspect-flyout-section">
          <div class="inspect-flyout-section-label">Masteries</div>
          <div class="inspect-flyout-masteries">${masteryHTML}</div>
        </div>
      ` : ''}
      ${skillsHTML ? `
        <div class="inspect-flyout-section">
          <div class="inspect-flyout-section-label">Skills</div>
          <div class="inspect-flyout-skills">${skillsHTML}</div>
        </div>
      ` : ''}
    `;
  }

  // ─── Adapters ───

  /** Build the PartyUnit shape that renderStrandShowcase reads from. */
  private toShowcaseUnit(u: InspectUnit, idx: number): PartyUnit {
    return {
      id: `inspect-${idx}`,
      unitClass: u.unitClass,
      name: u.name,
      race: u.race,
      element: u.element,
      defensiveTypes: u.defensiveTypes,
      hp: 0, spd: 0, equipped: true, rarity: u.rarity, itemLevel: u.level,
      level: u.level, experience: 0, xpIntoLevel: 0, xpToNextLevel: 0,
      unspentMasteryPoints: 0,
      masteryRanks: JSON.stringify(u.masteryRanks ?? {}),
      masteryCosts: '{}',
      implicitAffix: null, explicitAffixes: null,
      weaponId: null, armorId: null, accessoryId: null, offhandId: null,
      skill0Id: null, skill1Id: null, skill2Id: null, skill3Id: null,
      activeSkinId: u.activeSkinId,
      computedStats: u.computedStats,
    };
  }

  private toItemData(item: InspectItemData): ItemData {
    return { id: '', playerId: '', ...item, equippedToUnitId: null, inventorySlot: null, requirementsDisplay: '' };
  }
}
